import { DateLike, ModifyPartial, Vector3Like } from "type_aliases";
import addDateGetterAndSetterToDomainModel from "../../services/utilities/mixins/add_date_getter_and_setter_to_domain_model";
import { UniformatId } from "uniformat";
import SystemOfMeasurement from "./enums/system_of_measurement";
import { ApiInspectionModes } from "avvir";

export class PhotoViewerDetails {
  constructor(photoViewerDetails: Partial<PhotoViewerDetails> = {}) {
    const { photoSessionId, photoLocationId, photoSessionIds } = photoViewerDetails;
    this.photoSessionId = photoSessionId;
    this.photoLocationId = photoLocationId;
    this.photoSessionIds = photoSessionIds || [];
  }

  photoSessionId: number;
  photoLocationId: number;
  photoSessionIds: number[];
}

export class DeviationTolerance {
  constructor(tolerance: Partial<DeviationTolerance> = {}) {
    const { value, systemOfMeasurement } = tolerance;
    this.value = value;
    this.systemOfMeasurement = systemOfMeasurement;
  }

  value: number;
  systemOfMeasurement: SystemOfMeasurement;
}

class ViewTrades {
  constructor(trades: Partial<ViewTrades> = {}) {
    const { shownUniformatCodes } = trades;
    this.shownUniformatCodes = shownUniformatCodes;
  }

  shownUniformatCodes: UniformatId[];
}

type ViewCameraParameter = ModifyPartial<ViewCamera, { position: Vector3Like, target: Vector3Like }>

class ViewCamera {
  constructor(camera: ViewCameraParameter = {}) {
    const { position, target } = camera;
    this.position = new THREE.Vector3(position?.x, position?.y, position?.z);
    this.target = new THREE.Vector3(target?.x, target?.y, target?.z);
  }

  position: THREE.Vector3;
  target: THREE.Vector3;
}

class ViewFilters {
  constructor(filters: Partial<ViewFilters> = {}) {
    const { trades, deviationTolerance, pointCloudVisible, sectionPointCloud, onlyShowDeviationsFromCurrentScan } = filters;
    this.trades = new ViewTrades(trades);
    this.deviationTolerance = new DeviationTolerance(deviationTolerance);
    this.pointCloudVisible = pointCloudVisible;
    this.sectionPointCloud = sectionPointCloud;
    this.onlyShowDeviationsFromCurrentScan = onlyShowDeviationsFromCurrentScan;
  }

  trades: ViewTrades;
  deviationTolerance: DeviationTolerance;
  pointCloudVisible: boolean;
  sectionPointCloud: boolean;
  onlyShowDeviationsFromCurrentScan: boolean;
}

type SelectedElements = string[]
type ViewAttributesParameter = ModifyPartial<ViewAttributes, {
  camera: ViewCameraParameter
  selectionPosition: Vector3Like
  photoViewerDetails: Partial<PhotoViewerDetails>
  filters: Partial<ViewFilters>
}>

export class ViewAttributes {
  constructor(attributes: ViewAttributesParameter = {}) {
    const { camera, filters, selectionPosition, selectedElements, inspectionMode, photoViewerDetails } = attributes;
    this.camera = new ViewCamera(camera);
    this.filters = new ViewFilters(filters);
    this.selectionPosition = new THREE.Vector3(selectionPosition?.x, selectionPosition?.y, selectionPosition?.z);
    this.selectedElements = selectedElements || [];
    this.inspectionMode = inspectionMode;
    this.photoViewerDetails = new PhotoViewerDetails(photoViewerDetails);
  }

  camera: ViewCamera;
  filters: ViewFilters;
  selectionPosition: THREE.Vector3;
  selectedElements: SelectedElements;
  inspectionMode: ApiInspectionModes;
  photoViewerDetails: PhotoViewerDetails;
}

export type ViewParameter = ModifyPartial<View, { viewAttributes: ViewAttributesParameter, createdAt: DateLike }>
export default class View {
  constructor(view: ViewParameter = {}) {
    const { id, firebaseProjectId, firebaseFloorId, firebaseScanDatasetId, viewAttributes, createdBy, createdAt } = view;
    addDateGetterAndSetterToDomainModel(this, "createdAt");
    this.id = id;
    this.firebaseProjectId = firebaseProjectId;
    this.firebaseFloorId = firebaseFloorId;
    this.firebaseScanDatasetId = firebaseScanDatasetId;
    this.viewAttributes = new ViewAttributes(viewAttributes);
    this.createdBy = createdBy;
    // @ts-ignore
    this.createdAt = createdAt;
  }

  id: number;
  firebaseProjectId: string;
  firebaseFloorId: string;
  firebaseScanDatasetId: string;
  viewAttributes: ViewAttributes;
  createdBy: string;
  createdAt: Date;
}
